import React, { useEffect, useCallback } from 'react';
import { useStore } from 'react-redux';
import { Button, Box, Text, useToast } from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { permits, Requirement, refreshAccess, AccessRoot } from './access';
import useLogout from '../hooks/useLogout';
import { endpointAccess } from './endpointAccess';
import { writeAccessError, writeAccessMessage } from './writeAccess';
import { MutationHandle, useMutationLifecycle } from './useMutationLifecycle';

export const useCan = (requirement: Requirement) => useAppSelector(state => permits(state, requirement));
export const AccessGate: React.FC<{ action: Requirement }> = ({ action, children }) =>
  useCan(action) ? <>{children}</> : null;

const blocked = (error = { status: 'CUSTOM_ERROR', error: 'ACTION_UNAVAILABLE' }) => {
  return Object.assign(Promise.resolve({ error }), { unwrap: () => Promise.reject(error), abort: () => {}, reset: () => {} });
};
// Check the store at invocation time as well as hiding controls. A handler
// retained by an open form must not retain permission from an older render.
export const useAllowedMutation = <Args extends unknown[], Result extends MutationHandle,
  State extends { data?: unknown; error?: unknown; reset?: () => void }>(
  hook: () => readonly [(...args: Args) => Result, State],
  endpoint: string,
) => {
  const [originalTrigger, state] = hook();
  const [observe, operation] = useMutationLifecycle(state);
  const store = useStore<AccessRoot>();
  const userId = useAppSelector(root => root.auth?.profile?.userId);
  const toast = useToast();
  const trigger = useCallback((...args: Args) => {
    const current = store.getState();
    const result = endpointAccess[endpoint] === 'session' ? originalTrigger(...args)
      : current.auth?.profile?.userId !== userId ? blocked(writeAccessError('ACCESS_IDENTITY_CHANGED'))
      : permits(current, endpointAccess[endpoint] || '') ? originalTrigger(...args) : blocked();
    // Some editors intentionally ignore mutation results. A rejected queued
    // write must still explain why it was not sent.
    Promise.resolve(result).then(value => {
      const description = writeAccessMessage((value as { error?: unknown })?.error);
      if (description) toast({ title: 'Change not sent', description, status: 'error', isClosable: true });
    }, () => {});
    return observe(result, args[0]);
  }, [store, originalTrigger, endpoint, toast, userId, observe]);
  return [trigger, operation] as const;
};

export const AccessSession: React.FC = ({ children }) => {
  const dispatch = useAppDispatch();
  const store = useStore<AccessRoot>();
  useEffect(() => {
    dispatch(refreshAccess());
    const onFocus = () => {
      const status = store.getState().auth?.accessStatus;
      if (status === 'ready' || status === 'refreshing') dispatch(refreshAccess());
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [dispatch, store]);
  return <>{children}</>;
};
export const AccessError = () => {
  const { logout } = useLogout();
  const dispatch = useAppDispatch();
  return <Box p={8} role="alert"><Text>Could not verify your access to the admin panel.</Text>
    <Button onClick={() => dispatch(refreshAccess())}>Retry access check</Button><Button onClick={() => logout({})}>Sign out</Button></Box>;
};

export const withAccess = <P extends object>(Component: React.ComponentType<P>, requirement: Requirement | ((props: P) => Requirement)) => {
  const Guarded = (props: P) => <AccessGate action={typeof requirement === 'function' ? requirement(props) : requirement}>
    {React.createElement(Component, props)}
  </AccessGate>;
  return Guarded;
};
